import classNames from 'classnames';
import { AnimatePresence, m } from 'framer-motion';
import { useEffect, useId, useRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Checkbox from '@/components/molecules/checkbox/checkbox';
import Pill from '@/components/molecules/pill/pill';
import styles from './multi-select-dropdown.module.scss';

export interface MultiSelectDropdownItem<T = string> {
  /** Whether the item can be selected */
  isDisabled?: boolean;
  /** The label of the item */
  label: string;
  /** A secondary text shown right aligned, e.g. the number of results */
  subText?: string;
  /** The value of the item */
  value: T;
}

export interface MultiSelectDropdownProps<T = string> {
  /** Additional class names */
  className?: string;
  /** The icon shown before the label */
  icon?: IconName;
  /** Whether the dropdown is disabled */
  isDisabled?: boolean;
  /** The dropdown items */
  items?: MultiSelectDropdownItem<T>[];
  /** The dropdown label */
  label: string;
  /** The change handler, called with all selected values */
  onChange: (values: T[]) => void;
  /** The values of the selected items */
  values?: T[];
}

const MultiSelectDropdown = <ValueType,>({
  className,
  icon,
  isDisabled,
  items = [],
  label,
  onChange,
  values = [],
}: MultiSelectDropdownProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);
  // The item to focus once the list has rendered after opening it from the keyboard
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );
  const componentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemsId = useId();
  const isTriggerDisabled = isDisabled || items.length === 0;

  const enabledIndexes = items.reduce<number[]>(
    (indexes, item, index) => (item.isDisabled ? indexes : [...indexes, index]),
    [],
  );

  const handleToggle = (value: ValueType) => {
    onChange(
      values.includes(value)
        ? values.filter(selectedValue => selectedValue !== value)
        : [...values, value],
    );
  };

  const close = (shouldFocusTrigger = false) => {
    setIsOpen(false);
    setPendingFocusIndex(null);

    if (shouldFocusTrigger) triggerRef.current?.focus();
  };

  const firstEnabledIndex = enabledIndexes[0];
  const lastEnabledIndex = enabledIndexes[enabledIndexes.length - 1];

  const focusItem = (index: number | undefined) => {
    if (index !== undefined) itemRefs.current[index]?.focus();
  };

  // Moves focus to the next or previous enabled item, wrapping around at the ends
  const moveFocus = (step: 1 | -1) => {
    const currentPosition = enabledIndexes.indexOf(
      itemRefs.current.indexOf(document.activeElement as HTMLInputElement),
    );

    if (currentPosition === -1) {
      return focusItem(step === 1 ? firstEnabledIndex : lastEnabledIndex);
    }

    const nextPosition =
      (currentPosition + step + enabledIndexes.length) % enabledIndexes.length;

    focusItem(enabledIndexes[nextPosition]);
  };

  const handleTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();

    const targetIndex =
      event.key === 'ArrowDown' ? firstEnabledIndex : lastEnabledIndex;

    if (isOpen) return focusItem(targetIndex);

    setIsOpen(true);
    setPendingFocusIndex(targetIndex ?? null);
  };

  const handleItemsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1);
        break;

      case 'Home':
        event.preventDefault();
        focusItem(firstEnabledIndex);
        break;

      case 'End':
        event.preventDefault();
        focusItem(lastEnabledIndex);
        break;

      case 'Enter':
        // Checkboxes only toggle on space natively, Enter is supported for parity with ComboBox
        if (event.target instanceof HTMLInputElement) {
          event.preventDefault();
          event.target.click();
        }

        break;
    }
  };

  useEffect(() => {
    if (!isOpen || pendingFocusIndex === null) return;

    focusItem(pendingFocusIndex);
    setPendingFocusIndex(null);
  }, [isOpen, pendingFocusIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!componentRef.current?.contains(event.target as Node)) close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Only move focus back when it was inside the dropdown, so it is not stolen from elsewhere
      close(componentRef.current?.contains(document.activeElement) ?? false);
    };

    window.addEventListener('pointerdown', handlePointerDownOutside);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDownOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      ref={componentRef}
      className={classNames(styles.component, className, {
        [styles.isDisabled]: isTriggerDisabled,
        [styles.isOpen]: isOpen,
      })}
    >
      <button
        ref={triggerRef}
        aria-controls={isOpen ? itemsId : undefined}
        aria-expanded={isOpen}
        className={styles.trigger}
        disabled={isTriggerDisabled}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        onKeyDown={handleTriggerKeyDown}
        type="button"
      >
        {icon && <Icon name={icon} size={24} />}

        <Typography
          className={styles.label}
          tag="span"
          tagStyle="bodyMediumBold"
        >
          {label}
        </Typography>

        {values.length > 0 && (
          <Pill count={values.length} variant="secondary" />
        )}

        <Icon className={styles.chevron} name="ChevronDown" size={24} />
      </button>

      <AnimatePresence>
        {isOpen && items.length > 0 && (
          <m.div
            key="multi-select-dropdown-items"
            animate="show"
            aria-label={label}
            className={styles.items}
            exit="hide"
            id={itemsId}
            initial="hide"
            onKeyDown={handleItemsKeyDown}
            role="group"
            transition={{ duration: 0.2 }}
            variants={{
              hide: { opacity: 0, y: -8 },
              show: { opacity: 1, y: 0 },
            }}
          >
            {items.map((item, index) => {
              const isSelected = values.includes(item.value);

              return (
                <div
                  key={String(item.value)}
                  className={classNames(styles.item, {
                    [styles.isDisabled]: item.isDisabled,
                    [styles.isSelected]: isSelected,
                  })}
                >
                  <Checkbox
                    ref={element => {
                      itemRefs.current[index] = element;
                    }}
                    checked={isSelected}
                    className={styles.checkbox}
                    disabled={item.isDisabled}
                    label={item.label}
                    onChange={() => handleToggle(item.value)}
                    subText={item.subText}
                  />
                </div>
              );
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

MultiSelectDropdown.displayName = 'MultiSelectDropdown';

export default MultiSelectDropdown;
